import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  User,
  LogOut,
  Settings,
  ChevronDown,
  FileText,
  Home,
  UserCheck,
  LayoutDashboard,
  Briefcase,
  GraduationCap,
  Moon,
  Sun,
  Shield
} from 'lucide-react'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

interface UserMenuProps {
  userType?: 'candidate' | 'recruiter'
}

export default function UserMenu({ userType = 'candidate' }: UserMenuProps) {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const recruiterSettingsEnabled = useFeatureFlag('settingPage')

  useEffect(() => {
    const loadUserData = () => {
      if (typeof window !== 'undefined') {
        const userData = localStorage.getItem('currentUser')
        if (userData) {
          try {
            const user = JSON.parse(userData)
            setCurrentUser(user)
          } catch (error) {
            console.error('Error parsing user data:', error)
          }
        }

        const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
        const initialTheme = storedTheme || 'light'
        setTheme(initialTheme)
        applyTheme(initialTheme)
      }
    }

    loadUserData()

    const handleStorageChange = () => {
      loadUserData()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange)
      window.addEventListener('userDataUpdated', handleStorageChange)
      
      return () => {
        window.removeEventListener('storage', handleStorageChange)
        window.removeEventListener('userDataUpdated', handleStorageChange)
      }
    }
  }, [])

  const applyTheme = (nextTheme: 'light' | 'dark') => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      if (nextTheme === 'dark') {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }
    }
    localStorage.setItem('theme', nextTheme)
  }

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light'
    setTheme(next)
    applyTheme(next)
  }

  const getUserInitials = (user: any) => {
    if (!user) return userType === 'candidate' ? 'JS' : 'U'
    const firstName = user.firstName || user.first_name || ''
    const lastName = user.lastName || user.last_name || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || (userType === 'candidate' ? 'JS' : 'U')
  }

  const getUserDisplayName = (user: any) => {
    if (!user) return userType === 'candidate' ? 'Job Seeker' : 'User'
    const firstName = user.firstName || user.first_name || ''
    const lastName = user.lastName || user.last_name || ''
    return `${firstName} ${lastName}`.trim() || user.email || (userType === 'candidate' ? 'Job Seeker' : 'User')
  }

  const getRoleDisplayName = (user: any) => {
    if (!user || !user.role) return userType === 'candidate' ? 'Job Seeker' : 'User'
    const role = user.role.replace('_', ' ')
    return role.charAt(0).toUpperCase() + role.slice(1)
  }

  const getAdminAllowList = () => {
    const envList = (import.meta.env.VITE_ADMIN_EMAILS as string | undefined) || (import.meta.env.NEXT_PUBLIC_ADMIN_EMAILS as string | undefined)
    if (envList && typeof envList === 'string') {
      return envList.split(',').map(email => email.trim().toLowerCase()).filter(Boolean)
    }
    return []
  }

  const decodeJwt = (token?: string | null) => {
    if (!token) return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    try {
      return JSON.parse(atob(parts[1]))
    } catch (error) {
      console.warn('Unable to decode JWT payload')
      return null
    }
  }

  const isAdminUser = (user: any) => {
    const allowList = getAdminAllowList()
    const email = (user?.email || '').toLowerCase()
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
    const payload = decodeJwt(token)

    const roleFromProfile = (user?.role || '').toLowerCase()
    const roleFromToken = (payload?.role || payload?.userRole || '').toLowerCase()
    const isAdminRole = roleFromProfile === 'admin' || roleFromToken === 'admin' || payload?.is_admin === true

    if (isAdminRole) return true
    if (allowList.length === 0) return false
    return !!email && allowList.includes(email)
  }

  const getMenuItems = () => {
    if (userType === 'candidate') {
      return [
        { href: '/profile', icon: User, label: 'My Profile' },
        { href: '/candidate/career-profile', icon: GraduationCap, label: 'Career Profile' },
        { href: '/candidate/resume', icon: FileText, label: 'My Resume' },
        { href: '/candidate/settings', icon: Settings, label: 'Settings' },
        'separator',
        { href: '/candidate/dashboard', icon: Home, label: 'Job Seeker Dashboard' },
        { href: '/recruiter/dashboard', icon: UserCheck, label: 'Recruiter Dashboard' }
      ]
    } else {
      const recruiterItems: Array<{ href: string; icon: any; label: string } | 'separator'> = [
        { href: '/profile', icon: User, label: 'Profile' }
      ]

      if (isAdminUser(currentUser)) {
        recruiterItems.push({ href: '/admin', icon: Shield, label: 'Admin Console' })
      }

      if (recruiterSettingsEnabled) {
        recruiterItems.push({ href: '/recruiter/settings', icon: Settings, label: 'Settings' })
      }

      recruiterItems.push(
        'separator',
        { href: '/recruiter/dashboard', icon: LayoutDashboard, label: 'Recruiter Dashboard' },
        { href: '/jobs', icon: Briefcase, label: 'Job Search Board' }
      )

      return recruiterItems
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('currentUser')
    window.location.href = '/login'
  }

  if (!currentUser) {
    return (
      <div className="flex space-x-4">
        <Link to="/login" className="btn-secondary">
          Login
        </Link>
        <Link to="/register" className="btn-primary">
          Sign Up
        </Link>
      </div>
    )
  }

  const avatarColor = userType === 'candidate' ? 'bg-green-600' : 'bg-blue-600'

  return (
    <div className="relative">
      <button
        onClick={() => setShowUserMenu(!showUserMenu)}
        className="flex items-center space-x-2 p-1 lg:p-2 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <div className={`w-8 h-8 ${avatarColor} rounded-full flex items-center justify-center`}>
          <span className="text-white text-sm font-medium">{getUserInitials(currentUser)}</span>
        </div>
        <div className="hidden lg:block text-left">
          <p className="text-sm font-medium text-gray-900">{getUserDisplayName(currentUser)}</p>
          <p className="text-xs text-gray-500">{getRoleDisplayName(currentUser)}</p>
        </div>
        <ChevronDown className="hidden lg:block w-4 h-4 text-gray-600" />
      </button>
      
      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          >
            {getMenuItems().map((item, index) => {
              if (item === 'separator') {
                return (
                  <div key={`separator-${index}`} className="border-t border-gray-100 my-1" />
                )
              }
              
              const menuItem = item as { href: string; icon: any; label: string }
              const Icon = menuItem.icon
              
              return (
                <Link 
                  key={menuItem.href}
                  to={menuItem.href} 
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Icon className="w-4 h-4 mr-3" />
                  {menuItem.label}
                </Link>
              )
            })}
            
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={toggleTheme}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4 mr-3" /> : <Moon className="w-4 h-4 mr-3" />}
              {theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            </button>

            <div className="border-t border-gray-100 my-1" />
            <button 
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
